import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
    Activity, Loader2, LogIn, LogOut, Download, Upload, ChevronLeft, ChevronRight,
    RotateCcw, ArrowRight, CheckCircle2,
} from "lucide-react";

const ALL = "__all__";

const TYPE_META = {
    login: { label: "Accesso", icon: LogIn, cls: "bg-emerald-50 text-emerald-700" },
    logout: { label: "Uscita", icon: LogOut, cls: "bg-slate-100 text-slate-600" },
    download: { label: "Download", icon: Download, cls: "bg-blue-50 text-blue-700" },
    upload: { label: "Upload", icon: Upload, cls: "bg-amber-50 text-amber-700" },
};

const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
};

export default function ActivityLogPage() {
    const [employeeId, setEmployeeId] = useState(ALL);
    const [type, setType] = useState(ALL);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [employees, setEmployees] = useState([]);

    useEffect(() => {
        api.get("/employees").then((r) => {
            const list = [...r.data].sort((a, b) => `${a.cognome} ${a.nome}`.localeCompare(`${b.cognome} ${b.nome}`));
            setEmployees(list);
        }).catch(() => {});
    }, []);

    const params = useMemo(() => {
        const p = { page, page_size: 50 };
        if (employeeId !== ALL) p.employee_id = employeeId;
        if (type !== ALL) p.type = type;
        if (dateFrom) p.date_from = dateFrom;
        if (dateTo) p.date_to = dateTo;
        return p;
    }, [employeeId, type, dateFrom, dateTo, page]);

    useEffect(() => {
        setLoading(true);
        api.get("/activity/log", { params })
            .then((r) => setData(r.data))
            .catch(() => toast.error("Errore nel caricamento del registro"))
            .finally(() => setLoading(false));
    }, [params]);

    const resetFilters = () => {
        setEmployeeId(ALL); setType(ALL); setDateFrom(""); setDateTo(""); setPage(1);
    };
    const changeFilter = (setter) => (v) => { setter(v); setPage(1); };

    const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
    const t = data?.totals;

    return (
        <div className="mx-auto max-w-7xl px-6 py-8" data-testid="activity-log-page">
            <div className="mb-6 flex items-start gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                    <Activity className="h-6 w-6" />
                </div>
                <div>
                    <h1 className="font-display text-2xl font-bold text-slate-800">Registro attività dipendenti</h1>
                    <p className="text-sm text-slate-500">
                        Accessi, uscite, download e upload di file da parte di dipendenti e operatori
                    </p>
                </div>
            </div>

            {t && (
                <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {Object.entries(TYPE_META).map(([k, m]) => (
                        <div key={k} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid={`activity-kpi-${k}`}>
                            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                                <m.icon className="h-3 w-3" /> {m.label}
                            </div>
                            <div className="font-mono-numbers text-xl font-bold text-slate-800">{t[k]?.toLocaleString("it-IT") ?? 0}</div>
                        </div>
                    ))}
                </div>
            )}

            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                <Select value={employeeId} onValueChange={changeFilter(setEmployeeId)}>
                    <SelectTrigger className="h-9 w-[210px] text-sm" data-testid="activity-filter-employee">
                        <SelectValue placeholder="Dipendente" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                        <SelectItem value={ALL}>Tutti i dipendenti</SelectItem>
                        {employees.map((e) => (
                            <SelectItem key={e.id} value={e.id}>{e.cognome} {e.nome}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={type} onValueChange={changeFilter(setType)}>
                    <SelectTrigger className="h-9 w-[170px] text-sm" data-testid="activity-filter-type">
                        <SelectValue placeholder="Attività" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>Tutte le attività</SelectItem>
                        <SelectItem value="login">Accessi</SelectItem>
                        <SelectItem value="logout">Uscite</SelectItem>
                        <SelectItem value="download">Download</SelectItem>
                        <SelectItem value="upload">Upload</SelectItem>
                    </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">Dal</span>
                    <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                        className="h-9 w-[140px] rounded-md border border-slate-200 px-2 text-xs text-slate-600" data-testid="activity-date-from" />
                    <span className="text-xs text-slate-400">al</span>
                    <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                        className="h-9 w-[140px] rounded-md border border-slate-200 px-2 text-xs text-slate-600" data-testid="activity-date-to" />
                    <Button variant="ghost" size="icon" onClick={resetFilters} title="Azzera filtri" data-testid="activity-reset-filters">
                        <RotateCcw className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-300" />
                </div>
            ) : !data || data.items.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="activity-empty">
                    Nessuna attività trovata con i filtri selezionati.
                    <div className="mt-1 text-xs text-slate-400">Nota: accessi e uscite vengono registrati dal 29/07/2026 in poi.</div>
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    <th className="px-3 py-2.5">Data e ora</th>
                                    <th className="px-3 py-2.5">Utente</th>
                                    <th className="px-3 py-2.5">Attività</th>
                                    <th className="px-3 py-2.5">Dettaglio</th>
                                    <th className="px-3 py-2.5">IP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((ev, i) => {
                                    const m = TYPE_META[ev.type] || TYPE_META.download;
                                    return (
                                        <tr key={ev.id || `${ev.at}-${ev.type}-${i}`} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`activity-row-${i}`}>
                                            <td className="whitespace-nowrap px-3 py-2 font-mono-numbers text-xs text-slate-500">{fmtDateTime(ev.at)}</td>
                                            <td className="whitespace-nowrap px-3 py-2">
                                                {ev.user_id ? (
                                                    <Link to={`/dipendenti/${ev.user_id}`} className="flex items-center gap-1 font-semibold text-slate-700 hover:text-[#376DB3] hover:underline">
                                                        {ev.user_name || "—"}
                                                        <ArrowRight className="h-3 w-3 text-slate-300" />
                                                    </Link>
                                                ) : (ev.user_name || "—")}
                                                {ev.role === "operator" && (
                                                    <span className="ml-1.5 rounded-full bg-violet-50 px-1.5 py-px text-[10px] font-semibold text-violet-600">operatore</span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2">
                                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.cls}`}>
                                                    <m.icon className="h-3 w-3" /> {m.label}
                                                </span>
                                            </td>
                                            <td className="max-w-md truncate px-3 py-2 text-xs text-slate-600">
                                                {ev.detail || "—"}
                                                {ev.type === "download" && ev.acknowledged && (
                                                    <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-600">
                                                        <CheckCircle2 className="h-3 w-3" /> presa visione
                                                    </span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-2 font-mono-numbers text-xs text-slate-400">{ev.ip || "—"}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                        <span data-testid="activity-pagination-info">
                            {(data.page - 1) * data.page_size + 1}–{Math.min(data.page * data.page_size, data.total)} di {data.total.toLocaleString("it-IT")}
                        </span>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="activity-prev-page">
                                <ChevronLeft className="h-4 w-4" /> Prec.
                            </Button>
                            <span className="font-mono-numbers">pag. {data.page}/{totalPages}</span>
                            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="activity-next-page">
                                Succ. <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
